import { useEffect } from 'react';
import { Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectContentByDate,
  selectContentStatusByDate,
} from '../store/selectors/content-selectors';
import { fetchContent } from '../store/thunks/content-thunks';
import { AdminDayNav } from '../components/AdminDayNav';
import { AdminPendingForDate } from '../components/AdminPendingForDate';
import { ApiUnavailableBanner } from '../components/ApiUnavailableBanner';
import { EmptyResults } from '../components/EmptyResults';
import SeoHead from '../components/SeoHead';
// TODO(@dvgup/branding migration): replace with `@dvgup/branding`'s
// runtime export once TODO-013 lands. The current fixture supplies
// the `share.*` slot consumed by `<SeoHead>` below.
import { brandingFixture } from '../lib/branding/branding-fixture';
import { DailyLayout, isContentEmpty } from './DailyPage';

/**
 * Day-detail page surface (task 5.5, public-website spec).
 *
 * `<DayDetailPage>` at route `/daily/:date` reuses the shared
 * `DailyLayout` from `<DailyPage>` to render the same Requirement 3.1
 * section ordering for an arbitrary historical date. Per the task
 * spec, **no Requirement 1.6 fallback** is applied here — the user
 * explicitly asked for that date, so an empty payload renders
 * `<EmptyResults>` rather than silently substituting another day.
 *
 * Function-length budget: every export below stays under 20 lines per
 * Requirement 3.7.
 */

/**
 * Format an ISO-8601 calendar date as `MMMM d, yyyy` in UTC for the
 * SEO `<title>` on a day-detail route. Falls back to the raw
 * `YYYY-MM-DD` string when the input is unparseable so the route
 * always emits a stable title.
 */
function formatDayTitleDate(isoDate: string): string {
  if (!isoDate) return '';
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

/**
 * `<DayDetailPage>` — route `/daily/:date`. Dispatches `fetchContent`
 * for the parameterized date on mount + on param change. Renders the
 * shared `DailyLayout` for the resolved content,
 * `<ApiUnavailableBanner>` on failure (above any cached content), and
 * `<EmptyResults>` when the call resolved with no content.
 */
export default function DayDetailPage(): JSX.Element {
  const params = useParams<{ date: string }>();
  const date = params.date ?? '';
  const dispatch = useAppDispatch();
  const content = useAppSelector((s) => selectContentByDate(s, date));
  const status = useAppSelector((s) => selectContentStatusByDate(s, date));
  useEffect(() => {
    if (date) void dispatch(fetchContent({ date }));
  }, [dispatch, date]);
  return renderDayDetailBody({ status, content, date });
}

interface DayDetailBodyArgs {
  readonly status: 'idle' | 'loading' | 'succeeded' | 'failed';
  readonly content: ReturnType<typeof selectContentByDate>;
  readonly date: string;
}

function renderDayDetailBody(args: DayDetailBodyArgs): JSX.Element {
  const showBanner = args.status === 'failed';
  const showEmpty = args.status === 'succeeded' && isContentEmpty(args.content);
  const formattedDate = formatDayTitleDate(args.date);
  const hasContent = args.content && !isContentEmpty(args.content);
  // Per-record routes hydrate from the loaded ApprovedContent record:
  // when present, the day's `hookPhrase` / `primaryImage` populate
  // the `<meta description>` / `og:image` / `twitter:image`. The
  // `<title>` is always `Daily for <formatted date>` per the task
  // 10.3 contract.
  return (
    <Stack spacing={2}>
      <SeoHead
        route={`/daily/${args.date}`}
        pageTitle={`Daily for ${formattedDate}`}
        pageDescription={hasContent ? args.content?.hookPhrase : undefined}
        pageImage={hasContent ? args.content?.primaryImage : undefined}
        branding={brandingFixture.share}
        jsonLdType="Article"
      />
      {/*
       * Admin day-nav sits above every possible render state (banner,
       * layout, empty) so admins can navigate away from an empty
       * future date without a full re-render trip through
       * `<EmptyResults>` first. Renders nothing for non-admins.
       */}
      <AdminDayNav currentDate={args.date} />
      {showBanner ? <ApiUnavailableBanner /> : null}
      {args.content && !isContentEmpty(args.content) ? (
        <DailyLayout content={args.content} />
      ) : null}
      {showEmpty ? <EmptyResults /> : null}
      <AdminPendingForDate viewedDate={args.date} />
    </Stack>
  );
}
